import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchAiLlmConfig,
  saveAiLlmConfig,
  testAiLlmConfig,
  getDecisionApiError
} from '../../services/decisionApi';

const EMPTY = {
  enabled: false,
  base_url: '',
  model: '',
  api_key: '',
  clear_key: false
};

export default function AiSettingsPage() {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({ has_key: false, key_hint: null, updated_at: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAiLlmConfig();
      const cfg = data.config || {};
      setForm({
        enabled: Boolean(cfg.enabled),
        base_url: cfg.base_url || '',
        model: cfg.model || '',
        api_key: '',
        clear_key: false
      });
      setMeta({
        has_key: Boolean(cfg.has_key),
        key_hint: cfg.key_hint || null,
        updated_at: cfg.updated_at || null
      });
    } catch (err) {
      setError(getDecisionApiError(err, err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const body = {
        enabled: form.enabled,
        base_url: form.base_url,
        model: form.model
      };
      if (form.clear_key) body.clear_key = true;
      else if (form.api_key.trim()) body.api_key = form.api_key.trim();
      const data = await saveAiLlmConfig(body);
      const cfg = data.config || {};
      setForm((prev) => ({
        ...prev,
        enabled: Boolean(cfg.enabled),
        base_url: cfg.base_url || '',
        model: cfg.model || '',
        api_key: '',
        clear_key: false
      }));
      setMeta({
        has_key: Boolean(cfg.has_key),
        key_hint: cfg.key_hint || null,
        updated_at: cfg.updated_at || null
      });
      setMessage(cfg.enabled ? '已保存并启用自带模型' : '已保存（当前使用平台模型）');
    } catch (err) {
      setError(getDecisionApiError(err, err.message));
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    setTesting(true);
    setMessage('');
    setError('');
    try {
      const body = {};
      if (form.api_key.trim() && form.base_url.trim() && form.model.trim()) {
        body.api_key = form.api_key.trim();
        body.base_url = form.base_url.trim();
        body.model = form.model.trim();
      }
      const data = await testAiLlmConfig(body);
      setMessage(`连通成功（${data.model}，${data.latencyMs}ms）：${data.reply || 'pong'}`);
    } catch (err) {
      setError(getDecisionApiError(err, err.message));
    } finally {
      setTesting(false);
    }
  }

  return (
    <div>
      <h1 className="decision-page-title">模型设置</h1>
      <p className="decision-page-sub">
        默认使用平台智谱。也可填写自己的 OpenAI 兼容接口（OpenAI / DeepSeek / 自建网关等），费用由你的 Key 承担。
      </p>

      <div className="decision-card">
        {loading ? <p className="decision-muted">加载中…</p> : null}
        <form onSubmit={onSave}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            启用自带模型（BYOK）
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <div className="decision-muted">Base URL（需含 /v1）</div>
            <input
              className="decision-select"
              style={{ width: '100%' }}
              placeholder="https://api.openai.com/v1"
              value={form.base_url}
              onChange={(e) => setForm((prev) => ({ ...prev, base_url: e.target.value }))}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <div className="decision-muted">Model</div>
            <input
              className="decision-select"
              style={{ width: '100%' }}
              placeholder="gpt-4o-mini / deepseek-chat / glm-4-flash"
              value={form.model}
              onChange={(e) => setForm((prev) => ({ ...prev, model: e.target.value }))}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <div className="decision-muted">
              API Key
              {meta.has_key ? `（已保存 ${meta.key_hint || '****'}，留空表示不改）` : ''}
            </div>
            <input
              className="decision-select"
              style={{ width: '100%' }}
              type="password"
              autoComplete="off"
              placeholder={meta.has_key ? '••••••••' : 'sk-...'}
              value={form.api_key}
              onChange={(e) => setForm((prev) => ({ ...prev, api_key: e.target.value, clear_key: false }))}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={form.clear_key}
              onChange={(e) => setForm((prev) => ({ ...prev, clear_key: e.target.checked, api_key: '' }))}
            />
            清除已保存的 Key
          </label>

          {meta.updated_at ? (
            <p className="decision-muted">上次更新 {meta.updated_at}</p>
          ) : null}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="submit" className="decision-btn" disabled={saving || loading}>
              {saving ? '保存中…' : '保存'}
            </button>
            <button type="button" className="decision-btn ghost" onClick={() => void onTest()} disabled={testing || loading}>
              {testing ? '测试中…' : '连通测试'}
            </button>
          </div>
        </form>

        {message ? <p className="decision-muted" style={{ marginTop: 12 }}>{message}</p> : null}
        {error ? <p className="decision-error" style={{ marginTop: 12 }}>{error}</p> : null}

        <p className="decision-muted" style={{ marginTop: 16, fontSize: 13 }}>
          启用后对话走你的模型；失败不会静默回退平台。关闭 BYOK 后恢复平台智谱。Key 仅保存在腾讯决策库，接口不回传明文。
        </p>
      </div>
    </div>
  );
}
